import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { api } from '../api';
import { Aviso, Etiqueta, Painel } from '../comum';

/**
 * O que hoje tem de fora do normal deste parque.
 *
 * 🔴 **Anomalia não é "valor alto".** 400 máquinas sem patch num parque que
 * sempre teve 400 não aparece aqui — é o normal daquele cliente, e o briefing já
 * cuida disso. O que aparece é a quebra de padrão: o número que este parque
 * nunca teve.
 *
 * 🔴 **E "nenhuma anomalia" nunca é dito sem base.** Uma instalação de uma
 * semana não sabe o que é normal; a tela diz isso, em vez de tranquilizar quem
 * lê com uma afirmação que ninguém mediu.
 */

export interface Anomalia {
  readonly metrica: string;
  readonly rotulo: string;
  readonly dia: string;
  readonly valor: number;
  readonly mediana: number;
  readonly zRobusto: number | null;
  readonly desvioAbsoluto: number;
  readonly direcao: 'ACIMA' | 'ABAIXO';
  readonly severidade: 'MEDIUM' | 'HIGH';
  readonly baseDeDias: number;
}

interface Resposta {
  readonly anomalias: readonly Anomalia[];
  readonly baseDeDias: number;
  readonly aviso: string | null;
}

/** A frase que explica a anomalia sem exigir estatística de quem lê. */
export function frasePara(a: Anomalia): string {
  const sentido = a.direcao === 'ACIMA' ? 'acima' : 'abaixo';
  const base = `${a.valor} hoje, contra ${a.mediana} de mediana nos últimos ${a.baseDeDias} dias`;
  return a.zRobusto === null
    ? // Base constante: dizer "infinitos desvios" seria inventar um número.
      `${base}. O valor era constante até hoje.`
    : `${base} — ${Math.abs(a.zRobusto)} desvios ${sentido} do normal.`;
}

export function Anomalias({ aoPerderSessao }: { aoPerderSessao: () => void }): ReactNode {
  const [dados, setDados] = useState<Resposta | null>(null);

  const carregar = useCallback(async (): Promise<void> => {
    try {
      setDados(await api.get<Resposta>('/api/anomalias'));
    } catch (e: unknown) {
      if ((e as { precisaLogin?: boolean }).precisaLogin === true) aoPerderSessao();
    }
  }, [aoPerderSessao]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  if (dados === null) return null;

  // Sem anomalia e sem aviso não vale um painel vazio na tela.
  if (dados.anomalias.length === 0 && dados.aviso === null) return null;

  return (
    <Painel titulo="Fora do padrão" sub="O que hoje destoa do histórico deste parque.">
      {dados.anomalias.length === 0 && dados.aviso !== null && (
        <Aviso tipo="info">{dados.aviso}</Aviso>
      )}

      {dados.anomalias.map((a) => (
        <div key={`${a.metrica}-${a.dia}`} className="linha" style={{ padding: '6px 0' }}>
          <span>
            <strong>{a.rotulo}</strong> <span className="suave">· {frasePara(a)}</span>
          </span>
          <Etiqueta tom={a.severidade === 'HIGH' ? 'erro' : 'alerta'}>
            {a.direcao === 'ACIMA' ? '▲' : '▼'} fora do padrão
          </Etiqueta>
        </div>
      ))}
    </Painel>
  );
}
